import { useAuth } from '@clerk/clerk-react';
import { useCallback, useEffect, useState } from 'react';

const TOKEN_POLL_MS = 125;
const TOKEN_MAX_POLLS = 20;

/**
 * True only when Clerk is loaded, the user is signed in, and getToken() returns a JWT.
 * Avoids quota/API calls that run on isSignedIn before the session token exists.
 */
export function useClerkSessionReady() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const [tokenReady, setTokenReady] = useState(false);

  const waitForSessionToken = useCallback(async () => {
    if (!isLoaded || !isSignedIn) {
      return null;
    }
    for (let attempt = 0; attempt < TOKEN_MAX_POLLS; attempt += 1) {
      try {
        const token = await getToken();
        if (token) {
          return token;
        }
      } catch (err) {
        console.warn('[auth] getToken attempt failed:', err);
      }
      await new Promise((resolve) => {
        setTimeout(resolve, TOKEN_POLL_MS);
      });
    }
    return null;
  }, [isLoaded, isSignedIn, getToken]);

  useEffect(() => {
    let cancelled = false;

    if (!isLoaded || !isSignedIn) {
      setTokenReady(false);
      return undefined;
    }

    setTokenReady(false);

    (async () => {
      const token = await waitForSessionToken();
      if (!cancelled) {
        setTokenReady(Boolean(token));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, waitForSessionToken]);

  return {
    isClerkLoaded: Boolean(isLoaded),
    isSignedIn: Boolean(isSignedIn),
    tokenReady,
    /** All conditions required before protected API calls (e.g. /generation-quota). */
    isSessionReady: Boolean(isLoaded && isSignedIn && tokenReady),
    waitForSessionToken,
  };
}
